import React, { useState, useRef, useEffect } from 'react';
import logoImage from '../images/logo.svg'; // Update this path as needed
import auth from '../../shared/mobx/auth-state';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';

const dropDownNavs = [
  { text: "Dashboard", path: "/dashboard", access: ["user", "admin"] },
  { text: "My Profile", path: "/my-profile", access: ["user", "admin"] },
  { text: "Subscription Details", path: "/subscriptions", access: ["user"] },
  { text: "Subscription History", path: "/user-subscription-history", access: ["user"] },
  { text: "Logout", path: "/auth/login?preaction=logout", access: ["user", "admin"] },
];

interface NavbarProps {
  isIndex: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isIndex }) => {
  const [isIconOpen, setIconOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const navMenuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !(dropdownRef.current as HTMLElement).contains(event.target as Node)) {
      setIconOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isIconOpen]);

  const [activeHash, setActiveHash] = useState(window.location.hash || '#home');

  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash || '#home');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Scroll to the hash section when the location changes
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const handleNavigation = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(path);
  };

  // Custom navigation for hash links
  const handleNavClick = (hash: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsNavOpen(false);
    if (isIndex) {
      // Already on home page
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/${hash}`);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      // Simple, reliable scroll detection that works on all devices
      const scrollTop = Math.max(
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      
      // Use a lower threshold (15px) for mobile to trigger earlier
      const threshold = window.innerWidth <= 1024 ? 15 : 100;
      setIsScrolled(scrollTop >= threshold);
    };

    // Use both scroll and touchmove for mobile compatibility
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    
    // Check on mount
    handleScroll();
    
    // Also check on a small interval as fallback for mobile
    const checkInterval = setInterval(handleScroll, 50);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      clearInterval(checkInterval);
    };
  }, []);

  // Handle closing nav menu on outside click or scroll
  useEffect(() => {
    if (!isNavOpen) return;
    const handleClickOutsideNav = (event: MouseEvent) => {
      if (navMenuRef.current && !(navMenuRef.current as HTMLElement).contains(event.target as Node)) {
        setIsNavOpen(false);
      }
    };
    const handleScrollNav = () => setIsNavOpen(false);
    document.addEventListener('mousedown', handleClickOutsideNav);
    window.addEventListener('scroll', handleScrollNav);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideNav);
      window.removeEventListener('scroll', handleScrollNav);
    };
  }, [isNavOpen]);

  return (
    <>
      <span id="up" />
      <header className={`header webtopdf-wrap ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-md-3 col-sm-12 col-12">
              <div className="logo">
                <a href="/">
                  <img src={logoImage} width="240px" height="60px" className="img-responsive" alt="Logo" />
                </a>
              </div>
            </div>
            <div className="col-md-9 col-sm-12 col-12 mobile-wraper">
              <nav className="navbar navbar-expand-lg justify-content-center">
                <button
                  className="navbar-toggler"
                  type="button"
                  aria-controls="navbarNav"
                  aria-expanded={isNavOpen}
                  aria-label="Toggle navigation"
                  onClick={() => setIsNavOpen((prev) => !prev)}
                >
                  <span className="navbar-toggler-icon"></span>
                </button>
                <div
                  className={`collapse navbar-collapse justify-content-center${isNavOpen ? ' show' : ''}`}
                  id="navbarNav"
                  ref={navMenuRef}
                >
                  <ul className="navbar-nav">
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#home' ? 'active' : ''}`} href={isIndex ? "#home" : "/"} onClick={handleNavClick('#home')}>
                        Home
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#benefit' ? 'active' : ''}`} href={isIndex ? "#benefit" : "/#benefit"} onClick={handleNavClick('#benefit')}>
                        Benefits
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#reviews' ? 'active' : ''}`} href={isIndex ? "#reviews" : "/#reviews"} onClick={handleNavClick('#reviews')}>
                        Reviews
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#pricing' ? 'active' : ''}`} href={isIndex ? "#pricing" : "/#pricing"} onClick={handleNavClick('#pricing')}>
                        Pricing
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#faq' ? 'active' : ''}`} href={isIndex ? "#faq" : "/#faq"} onClick={handleNavClick('#faq')}>
                        FAQs
                      </a>
                    </li>
                    <li className="nav-item">
                      <a href="https://webs2pdf.com/blog/" className='nav-link'>Blog</a>
                    </li>
                    <li className="nav-item">
                      <a className={`nav-link ${activeHash === '#contact' ? 'active' : ''}`} href={isIndex ? "#contact" : "/#contact"} onClick={handleNavClick('#contact')}>
                        Contact
                      </a>
                    </li>
                    <ul className="login-menu">
                      {dropDownNavs.map(
                        (item) =>
                          item.access.includes(auth.activeRole) && (
                            <li key={item.text} className="nav-items">
                              <a href={item.path} className="nav-links" onClick={handleNavigation(item.path)}>
                                {item.text}
                              </a>
                            </li>
                          )
                      )}
                    </ul>
                    <div className="sign-btn mobile-show">
                      {auth.isLoggedIn() ? (
                        <button
                          ref={dropdownRef}
                          onClick={() => setIconOpen(!isIconOpen)}
                          className={`circle-button ${isIconOpen ? 'toggleUserIcon' : ''}`}
                          style={{ backgroundColor: auth.currentUser?.profileImage ? 'transparent' : undefined }}
                        >
                          {auth.currentUser?.profileImage ? (
                            <img
                              width="40"
                              height="40"
                              style={{ width: '40px', height: '40px', display: 'block', borderRadius: 'inherit' }}
                              src={auth.currentUser?.profileImage}
                              alt="Profile"
                            />
                          ) : (
                            <i className="fas fa-user user-icon"></i>
                          )}
                          {isIconOpen && (
                            <ul className="dropdown-menus">
                              {dropDownNavs.map(
                                (item) =>
                                  item.access.includes(auth.activeRole) && (
                                    <li key={item.text} className="dropdown-item">
                                      <a href={item.path} onClick={handleNavigation(item.path)}>
                                        {item.text}
                                      </a>
                                    </li>
                                  )
                              )}
                            </ul>
                          )}
                        </button>
                      ) : (
                        <Link className="nav-link" to="/auth/login">
                          Sign In
                        </Link>
                      )}
                    </div>
                  </ul>
                </div>
              </nav>
              <div className="sign-btn">
                {auth.isLoggedIn() ? (
                  <button
                    ref={dropdownRef}
                    onClick={() => setIconOpen(!isIconOpen)}
                    className={`circle-button ${isIconOpen ? 'toggleUserIcon' : ''}`}
                    style={{ backgroundColor: auth.currentUser?.profileImage ? 'transparent' : undefined }}
                  >
                    {auth.currentUser?.profileImage ? (
                      <img
                        width="40"
                        height="40"
                        style={{ width: '40px', height: '40px', display: 'block', borderRadius: 'inherit' }}
                        src={auth.currentUser?.profileImage}
                        alt="Profile"
                      />
                    ) : (
                      <i className="fas fa-user user-icon"></i>
                    )}
                    {isIconOpen && (
                      <ul className="dropdown-menus">
                        {dropDownNavs.map(
                          (item) =>
                            item.access.includes(auth.activeRole) && (
                              <li key={item.text} className="dropdown-item">
                                <a href={item.path} onClick={handleNavigation(item.path)}>
                                  {item.text}
                                </a>
                              </li>
                            )
                        )}
                      </ul>
                    )}
                  </button>
                ) : (
                  <Link className="nav-link" to="/auth/login">
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default observer(Navbar);
